import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AssetDisplay } from "@/components/ui/asset-display";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  XAxis,
  YAxis,
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceDot,
} from "recharts";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { ArrowUpRight, ArrowDownRight, TrendingUp, TrendingDown } from "lucide-react";
import type { Asset, AssetChart, Record as TradeRecord } from "@/schemas";
import { fetchWithAuth } from "@/lib/api";
import { API_BASE } from "@/config/api";
import { getRecordsByBookAndAsset, getGlobalRecordsByAsset } from "../services/portfolio-service";

interface AssetTradeModalProps {
  asset: Asset | null;
  bookId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface TradeMarker {
  timestamp: string;
  price: number;
  type: "buy" | "sell";
  quantity: number;
  date: Date;
}

const chartConfig = {
  price: {
    label: "Price",
    color: "hsl(var(--primary))",
  },
} satisfies ChartConfig;

export function AssetTradeModal({
  asset,
  bookId,
  open,
  onOpenChange,
}: AssetTradeModalProps) {
  const [chartData, setChartData] = useState<AssetChart | null>(null);
  const [records, setRecords] = useState<TradeRecord[]>([]);
  const [loading, setLoading] = useState(true);

  const isGlobal = bookId === "global";

  useEffect(() => {
    if (!open || !asset) {
      setChartData(null);
      setRecords([]);
      setLoading(true);
      return;
    }

    const fetchData = async () => {
      setLoading(true);

      try {
        // Fetch records first to determine the chart period
        const recordsRes = isGlobal
          ? await getGlobalRecordsByAsset(asset.asset_id)
          : await getRecordsByBookAndAsset(bookId, asset.asset_id);

        setRecords(recordsRes);

        // Determine chart period based on first trade date
        // Use "max" to ensure we see all trades, or fallback to "5y"
        const chartRes = await fetchWithAuth(
          `${API_BASE}/charts/${asset.asset_id}?period=max`
        );

        if (chartRes.ok) {
          const chart = await chartRes.json();
          
          // Filter chart data to start from first trade date (if trades exist)
          if (recordsRes.length > 0 && chart?.price_points?.length > 0) {
            const sortedRecords = [...recordsRes].sort(
              (a, b) => new Date(a.record_date).getTime() - new Date(b.record_date).getTime()
            );
            const firstTradeDate = new Date(sortedRecords[0].record_date);
            // Add a small buffer (7 days before first trade)
            firstTradeDate.setDate(firstTradeDate.getDate() - 7);
            
            chart.price_points = chart.price_points.filter(
              (p: { timestamp: string }) => new Date(p.timestamp) >= firstTradeDate
            );
          }
          
          setChartData(chart);
        }
      } catch (error) {
        console.error("Error fetching modal data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [open, asset, bookId, isGlobal]);


  // Convert records to markers on the chart
  const markers = useMemo((): TradeMarker[] => {
    if (!chartData?.price_points || records.length === 0) return [];

    const result: TradeMarker[] = [];

    for (const record of records) {
      const recordDate = new Date(record.record_date);
      const recordTimestamp = recordDate.getTime();

      // Find closest chart point
      let closestPoint = chartData.price_points[0];
      let minDiff = Infinity;

      for (const point of chartData.price_points) {
        const pointTime = new Date(point.timestamp).getTime();
        const diff = Math.abs(pointTime - recordTimestamp);
        if (diff < minDiff) {
          minDiff = diff;
          closestPoint = point;
        }
      }

      // Determine if buy or sell based on asset_id_buy/sell matching our asset
      const isBuy = record.asset_id_buy === asset?.asset_id;
      const isSell = record.asset_id_sell === asset?.asset_id;

      if (isBuy || isSell) {
        result.push({
          timestamp: closestPoint.timestamp,
          price: closestPoint.price,
          type: isBuy ? "buy" : "sell",
          quantity: isBuy ? record.qty_buy || 0 : record.qty_sell || 0,
          date: recordDate,
        });
      }
    }

    return result;
  }, [chartData, records, asset]);

  // Merge chart data with markers for display
  const mergedData = useMemo(() => {
    if (!chartData?.price_points) return [];

    return chartData.price_points.map((point) => {
      const marker = markers.find((m) => m.timestamp === point.timestamp);
      return {
        ...point,
        markerType: marker?.type,
        markerQty: marker?.quantity,
        markerDate: marker?.date,
      };
    });
  }, [chartData, markers]);

  const formatXAxis = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };

  const formatYAxis = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(value);
  };

  const yAxisDomain = useMemo(() => {
    if (!chartData?.price_points?.length) return [0, 100];
    const prices = chartData.price_points.map((p) => p.price);
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const padding = (max - min) * 0.1;
    return [Math.max(0, min - padding), max + padding];
  }, [chartData]);

  // Stats
  const buyCount = markers.filter((m) => m.type === "buy").length;
  const sellCount = markers.filter((m) => m.type === "sell").length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto overflow-x-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            {asset && <AssetDisplay asset={asset} size="default" />}

          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Trade summary */}
          <div className="flex gap-4">
            <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20">
              <ArrowUpRight className="h-3 w-3 mr-1" />
              {buyCount} Buy{buyCount !== 1 ? "s" : ""}
            </Badge>
            <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/20">
              <ArrowDownRight className="h-3 w-3 mr-1" />
              {sellCount} Sell{sellCount !== 1 ? "s" : ""}
            </Badge>
          </div>

          {/* Chart with markers */}
          <div className="h-80 w-full">
            {loading ? (
              <Skeleton className="h-full w-full" />
            ) : chartData?.price_points?.length ? (
              <ChartContainer config={chartConfig} className="h-full w-full !aspect-auto max-w-full overflow-hidden">
                <AreaChart
                  data={mergedData}
                  margin={{ top: 10, left: 10, right: 20, bottom: 5 }}
                >
                  <defs>
                    <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                    opacity={0.3}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="timestamp"
                    tickFormatter={formatXAxis}
                    tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    interval="preserveStartEnd"
                    minTickGap={50}
                  />
                  <YAxis
                    domain={yAxisDomain}
                    tickFormatter={formatYAxis}
                    tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    width={55}
                  />
                  <ChartTooltip
                    cursor={{ stroke: "hsl(var(--muted-foreground))", strokeWidth: 1, strokeDasharray: "4 4" }}
                    content={<ChartTooltipContent />}
                  />
                  <Area
                    type="monotone"
                    dataKey="price"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    fill="url(#colorPrice)"
                    dot={false}
                  />
                  {/* Buy markers */}
                  {markers
                    .filter((m) => m.type === "buy")
                    .map((marker, idx) => (
                      <ReferenceDot
                        key={`buy-${idx}`}
                        x={marker.timestamp}
                        y={marker.price}
                        r={6}
                        fill="#10b981"
                        stroke="#fff"
                        strokeWidth={2}
                      />
                    ))}
                  {/* Sell markers */}
                  {markers
                    .filter((m) => m.type === "sell")
                    .map((marker, idx) => (
                      <ReferenceDot
                        key={`sell-${idx}`}
                        x={marker.timestamp}
                        y={marker.price}
                        r={6}
                        fill="#ef4444"
                        stroke="#fff"
                        strokeWidth={2}
                      />
                    ))}
                </AreaChart>
              </ChartContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                No chart data available
              </div>
            )}
          </div>

          {/* Trade list */}
          {markers.length > 0 && (
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium mb-3">Trade History</h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {markers
                  .sort((a, b) => b.date.getTime() - a.date.getTime())
                  .map((marker, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between py-2 border-b last:border-0"
                    >
                      <div className="flex items-center gap-2">
                        {marker.type === "buy" ? (
                          <TrendingUp className="h-4 w-4 text-emerald-500" />
                        ) : (
                          <TrendingDown className="h-4 w-4 text-red-500" />
                        )}
                        <span className="text-sm font-medium capitalize">
                          {marker.type}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {marker.date.toLocaleDateString()}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-sm font-mono">
                          {marker.quantity.toFixed(2)} shares
                        </span>
                        <p className="text-xs text-muted-foreground">
                          @ ${marker.price.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
